import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API, useAuth } from '../auth';
import { useLanguage } from '../i18n';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import { toast } from 'sonner';
import { Plus, Minus, ShoppingCart, CheckCircle, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import OrderSuccessView from './OrderSuccessView';

export default function NewOrderPage() {
  const { t, getProductName, getCategoryName } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState('');
  const [cart, setCart] = useState({}); // {productId: quantity}
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [company, setCompany] = useState(null);

  useEffect(() => {
    if (user?.company_id) fetchData();
  }, [user]);

  const fetchData = async () => {
    try {
      const [catsRes, prodsRes, locsRes, compRes] = await Promise.all([
        axios.get(`${API}/product-categories`),
        axios.get(`${API}/products`),
        axios.get(`${API}/delivery-locations/company/${user.company_id}`),
        axios.get(`${API}/companies/${user.company_id}`),
      ]);
      setCategories(catsRes.data);
      setProducts(prodsRes.data);
      setLocations(locsRes.data);
      setCompany(compRes.data);
      if (locsRes.data.length > 0) setSelectedLocation(locsRes.data[0].id);
    } catch (err) {
      console.error(err);
    }
  };

  const updateQty = (productId, delta) => {
    setCart(prev => {
      const current = prev[productId] || 0;
      const newQty = Math.max(0, current + delta);
      if (newQty === 0) {
        const { [productId]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [productId]: newQty };
    });
  };

  const setQty = (productId, qty) => {
    const num = Math.max(0, parseInt(qty) || 0);
    if (num === 0) {
      setCart(prev => {
        const { [productId]: _, ...rest } = prev;
        return rest;
      });
    } else {
      setCart(prev => ({ ...prev, [productId]: num }));
    }
  };

  const formatCurrency = (val) => `₩${Number(val).toLocaleString('ko-KR')}`;

  const calculateTotals = () => {
    let subtotal = 0;
    let totalBoxes = 0;
    Object.entries(cart).forEach(([pid, qty]) => {
      const p = products.find(x => x.id === pid);
      if (p) {
        subtotal += p.base_price * qty;
        totalBoxes += qty;
      }
    });
    const deliveryCost = company?.delivery_method === 'paid_delivery' ? totalBoxes * 3000 :
                        company?.delivery_method === 'pickup' ? 0 : 0;
    return { subtotal, deliveryCost, total: subtotal + deliveryCost, totalBoxes };
  };

  const handleSubmit = async () => {
    if (!selectedLocation) {
      toast.error('Please select delivery location');
      return;
    }
    if (Object.keys(cart).length === 0) {
      toast.error('Add at least one product');
      return;
    }
    setSubmitting(true);
    try {
      const items = Object.entries(cart).map(([product_id, ordered_quantity]) => ({
        product_id, ordered_quantity
      }));
      const res = await axios.post(`${API}/orders`, {
        delivery_location_id: selectedLocation,
        items,
        notes,
      });
      setSuccess(res.data);
      setCart({});
      setNotes('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Order failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return <OrderSuccessView success={success} t={t} navigate={navigate} setSuccess={setSuccess} />;
  }

  const { subtotal, deliveryCost, total, totalBoxes } = calculateTotals();

  return (
    <div className="space-y-6" data-testid="new-order-page">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="back-btn">
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <p className="eyebrow text-[#556B2F] mb-1">{company?.company_name}</p>
          <h1 className="h-display text-3xl md:text-4xl">{t('newOrder')}</h1>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Products List */}
        <div className="lg:col-span-2 space-y-4">
          {/* Delivery Location */}
          <Card className="p-5">
            <Label className="eyebrow">{t('deliveryLocation')}</Label>
            <Select value={selectedLocation} onValueChange={setSelectedLocation}>
              <SelectTrigger className="mt-2 h-11" data-testid="location-select">
                <SelectValue placeholder="Select location" />
              </SelectTrigger>
              <SelectContent>
                {locations.map(loc => (
                  <SelectItem key={loc.id} value={loc.id} data-testid={`location-${loc.id}`}>
                    {loc.location_name} - {loc.address}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Card>

          {/* LAVASH brand section header */}
          <div className="flex items-center justify-between pt-2">
            <div>
              <span className="brand-tag-lavash mb-2 inline-block">LAVASH</span>
              <p className="text-sm text-[#556B2F]/70 mt-1">{t('lavashTagLine')}</p>
            </div>
          </div>

          {/* Products by Category */}
          {categories.map(cat => {
            const catProducts = products.filter(p => p.category_id === cat.id);
            if (catProducts.length === 0) return null;
            return (
              <Card key={cat.id} className="p-5 border-[#A4B18E]/30">
                <h3 className="text-lg font-bold text-[#1A1A1A] mb-3">{getCategoryName(cat)}</h3>
                <div className="space-y-3">
                  {catProducts.map(p => (
                    <div key={p.id} className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3 border border-[#1A1A1A]/8 rounded-md hover:border-[#556B2F]/40 transition-colors" data-testid={`product-${p.id}`}>
                      <div className="flex-1">
                        <p className="font-semibold text-[#1A1A1A]">{getProductName(p)}</p>
                        <p className="text-sm text-[#1A1A1A]/55 cff-text-numeric">
                          {formatCurrency(p.base_price)} / {p.unit_type === 'box' ? t('boxes') : t('packs')}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button variant="outline" size="icon" className="h-10 w-10" onClick={() => updateQty(p.id, -1)} disabled={!cart[p.id]} data-testid={`minus-${p.id}`}>
                          <Minus className="h-4 w-4" />
                        </Button>
                        <Input type="number" min="0" value={cart[p.id] || ''} onChange={(e) => setQty(p.id, e.target.value)} placeholder="0" className="w-16 h-10 text-center cff-text-numeric" data-testid={`qty-${p.id}`} />
                        <Button variant="outline" size="icon" className="h-10 w-10" onClick={() => updateQty(p.id, 1)} data-testid={`plus-${p.id}`}>
                          <Plus className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            );
          })}

          {/* VELORA Coming Soon section */}
          <div className="flex items-center justify-between pt-4">
            <div>
              <span className="brand-tag-velora inline-block">VELORA</span>
              <p className="text-sm text-[#6E4D3A]/70 mt-1 font-playfair italic">{t('veloraTagLine')}</p>
            </div>
          </div>
          <Card className="p-8 bg-[#F6E9E5]/60 border-[#E8C5C8] text-center" data-testid="velora-coming-soon">
            <p className="font-playfair italic text-3xl text-[#8E2A4E] mb-2">{t('comingSoon')}</p>
            <p className="text-sm text-[#6E4D3A]/70 max-w-sm mx-auto">
              {t('veloraComingSoonHint')}
            </p>
          </Card>

          <Card className="p-5">
            <Label className="eyebrow">{t('notes')}</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Special instructions..." className="mt-2" data-testid="order-notes" />
          </Card>
        </div>

        {/* Order Summary */}
        <div className="lg:col-span-1">
          <Card className="p-5 sticky top-20">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <ShoppingCart className="h-5 w-5" /> Cart
            </h3>
            {Object.keys(cart).length === 0 ? (
              <p className="text-sm text-gray-500 py-4">Cart is empty</p>
            ) : (
              <div className="space-y-2 mb-4 max-h-60 overflow-y-auto">
                {Object.entries(cart).map(([pid, qty]) => {
                  const p = products.find(x => x.id === pid);
                  if (!p) return null;
                  return (
                    <div key={pid} className="flex justify-between text-sm">
                      <span className="text-gray-700">{getProductName(p)} × {qty}</span>
                      <span className="font-medium cff-text-numeric">{formatCurrency(p.base_price * qty)}</span>
                    </div>
                  );
                })}
              </div>
            )}

            <div className="border-t border-gray-200 pt-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">{t('subtotal')}</span>
                <span className="cff-text-numeric font-medium">{formatCurrency(subtotal)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">{t('deliveryCost')}</span>
                <span className="cff-text-numeric font-medium">{formatCurrency(deliveryCost)}</span>
              </div>
              <div className="flex justify-between text-lg pt-2 border-t border-gray-100">
                <span className="font-semibold">{t('total')}</span>
                <span className="font-bold cff-text-numeric">{formatCurrency(total)}</span>
              </div>
            </div>

            <Button
              onClick={handleSubmit}
              disabled={submitting || Object.keys(cart).length === 0 || !selectedLocation}
              className="w-full mt-5 h-12 cff-button-primary"
              data-testid="submit-order-btn"
            >
              {submitting ? '...' : t('placeOrder')}
            </Button>
          </Card>
        </div>
      </div>
    </div>
  );
}
